import React, { useState, useEffect, useRef } from 'react';
import supabase from '../config/supabase';
import { HelpInfoButton } from './HelpTooltip';
import {
  assignVendorToItem,
  createVendorAliasIfNeeded,
  normalizeVendorName,
  renameVendorAndCreateAlias
} from '../utils/vendorResolution';
import { cycleToBitmask, bitmaskToCycle } from './CycleSelector';
import {
  saveVendorContactAndPropagate,
  fetchPurchaseOrders,
  resolveUserProfileCompactName,
  formatPoCurrency,
} from '../services/purchaseOrderService';
import { statusPillStyle, ORDER_STATUS_STYLES } from './ordering/orderingStyles';
import useHideMobileNav from '../utils/useHideMobileNav';
import {
  ANSWERED_SOURCES,
  DELIVERY_ANSWER_LABEL,
  PICKUP_ANSWER_LABEL,
  buyingModePill,
  buyingModeSourceText,
  buyingModeExplanation,
} from '../utils/buyingMode';

// ── Purchase-order metadata helpers ──────────────────────────────────────────
// A PO is treated as an order that was actually sent to the vendor when it has
// a send timestamp, or its delivery_status is anything other than the
// not-yet-sent states (draft / blocked / awaiting approval). Everything else
// (sent, delivered, received, canceled, voided, replaced, …) represents a real
// order that left the building and therefore belongs in the vendor's history.
const PO_NOT_SENT_STATUSES = new Set(['draft', 'blocked', 'blocked_no_email', 'pending_approval']);
const wasPurchaseOrderSent = (po) => (
  !!po?.sent_at || (!!po?.delivery_status && !PO_NOT_SENT_STATUSES.has(po.delivery_status))
);
const purchaseOrderTime = (po) => {
  const raw = po?.sent_at || po?.created_at;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

// Reduce a full name to "First L." — never a bare user id / email.
const toFirstLast = (name) => {
  const n = String(name || '').trim();
  if (!n || n.includes('@')) return '';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const lastInitial = parts[parts.length - 1][0];
  return `${parts[0]} ${lastInitial.toUpperCase()}.`;
};

// Human-readable fallback derived from an email local-part, e.g.
// "john.doe@x.com" → "John Doe". Used only when we have no real name.
const prettyEmailLocal = (email) => {
  const local = String(email || '').split('@')[0].trim();
  if (!local) return '';
  return local
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const formatPoDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(value);
  }
};

// Small inline icons (feather.replace() doesn't run on React-rendered nodes).
const iconBase = { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const PersonIcon = () => (
  <svg {...iconBase} aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
);
const MailIcon = () => (
  <svg {...iconBase} aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></svg>
);
const ReceiptIcon = () => (
  <svg {...iconBase} aria-hidden="true"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1V2l-2 1-2-1-2 1-2-1-2 1-2-1Z" /><path d="M8 7h8M8 11h8M8 15h5" /></svg>
);
const ClockIcon = () => (
  <svg {...iconBase} aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
);

// Load vendor editor stylesheet dynamically
const loadVendorStyles = () => {
  if (document.getElementById('ve-styles')) return;
  const link = document.createElement('link');
  link.id = 've-styles';
  link.rel = 'stylesheet';
  link.href = '/vendor-editor-styles.css';
  document.head.appendChild(link);
};

// ── Fees & charges helpers ───────────────────────────────────────────────────
// The numeric fee columns the "Fees & charges" section edits. The flat columns
// stay the single owner of VALUES; vendors.fee_provenance only records where
// each value came from ({source:'user'|'invoice', set_at, …} keyed by column).
export const VENDOR_FEE_COLUMNS = Object.freeze([
  'minimum_order_amount',
  'below_minimum_fee',
  'delivery_fee',
  'free_delivery_threshold',
  'fuel_surcharge_amount',
  'sales_tax_rate',
]);

// Columns added by the invoice-additional-charges migration. A store that is
// behind on migrations rejects writes to these; the save path strips them and
// retries once rather than failing the whole vendor save.
const NEW_FEE_COLUMNS = Object.freeze(['fuel_surcharge_amount', 'sales_tax_rate', 'fee_provenance']);

// Empty input means "unknown" (NULL) — never 0. An explicit 0 is a real
// answer ("no delivery fee") and is preserved. Money values round to 2dp.
export const parseFeeAmountInput = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/[$,\s]/g, '');
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
};

// Sales tax rate is DISPLAYED as a percent (8.25) but STORED as a fraction
// (0.0825), 4dp. Empty → null (unknown); explicit 0 → 0 (charges no tax).
// Bounded at 50% to match the DB CHECK (vendors_sales_tax_rate_range ≤ 0.5) —
// out-of-range input becomes null rather than a raw constraint failure.
export const feePercentToFraction = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).replace(/[%\s]/g, '');
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0 || n > 50) return null;
  return Math.round((n / 100) * 10000) / 10000;
};

export const feeFractionToPercent = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * 100 * 100) / 100);
};

// Plain-words provenance under a fee field, mirroring buyingModeSourceText:
//   no value                → "Not set yet"
//   entry.source 'user'     → "You set this"
//   entry.source 'invoice'  → "From invoice {number} · {date}" (fall back to
//                             "Detected from an invoice")
//   value but no entry      → null (legacy/unknown provenance: say nothing)
export const feeProvenanceText = (entry, value) => {
  const hasValue = !(value === null || value === undefined || String(value).trim() === '');
  if (!hasValue) return 'Not set yet';
  if (!entry || typeof entry !== 'object') return null;
  if (entry.source === 'user') return 'You set this';
  if (entry.source === 'invoice') {
    if (entry.invoice_number) {
      const date = formatPoDate(entry.set_at);
      return date
        ? `From invoice ${entry.invoice_number} · ${date}`
        : `From invoice ${entry.invoice_number}`;
    }
    return 'Detected from an invoice';
  }
  return null;
};

const normalizeFeeComparisonValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// For each fee column whose value CHANGED against the previous vendor row,
// merge a {source:'user', set_at} entry onto the existing fee_provenance map
// (preserving 'invoice' entries on untouched columns). Returns undefined when
// nothing changed, so an unrelated save never rewrites provenance.
export const mergeFeeProvenance = (previousVendor, nextValues, nowIso) => {
  const base = previousVendor
    && previousVendor.fee_provenance
    && typeof previousVendor.fee_provenance === 'object'
    ? previousVendor.fee_provenance
    : {};
  const merged = { ...base };
  let changed = false;
  const setAt = nowIso || new Date().toISOString();
  VENDOR_FEE_COLUMNS.forEach((column) => {
    if (!nextValues || !(column in nextValues)) return;
    const prev = normalizeFeeComparisonValue(previousVendor ? previousVendor[column] : null);
    const next = normalizeFeeComparisonValue(nextValues[column]);
    const same = (prev === null && next === null)
      || (prev !== null && next !== null && Math.abs(prev - next) < 1e-9);
    if (same) return;
    merged[column] = { source: 'user', set_at: setAt };
    changed = true;
  });
  return changed ? merged : undefined;
};

// Only the fee columns whose value CHANGED against the row the editor opened
// with. Untouched columns stay out of the update payload entirely, so a fee an
// invoice captured while the form sat open survives an unrelated save — the
// same lost-update discipline mergeFeeProvenance applies to provenance.
// Deliberate clears still ride: a value at open, blanked now, compares as
// changed and lands as null.
export const pickChangedFeeValues = (previousVendor, nextValues) => {
  const changed = {};
  VENDOR_FEE_COLUMNS.forEach((column) => {
    if (!nextValues || !(column in nextValues)) return;
    const prev = normalizeFeeComparisonValue(previousVendor ? previousVendor[column] : null);
    const next = normalizeFeeComparisonValue(nextValues[column]);
    const same = (prev === null && next === null)
      || (prev !== null && next !== null && Math.abs(prev - next) < 1e-9);
    if (!same) changed[column] = nextValues[column];
  });
  return changed;
};

// PostgREST reports a column missing from the schema cache as PGRST204 (or raw
// Postgres 42703) with the column named in the message. Only errors naming one
// of the NEW fee columns trigger the strip-and-retry — a missing legacy column
// is a different problem and must still surface.
export const isMissingFeeColumnError = (error) => {
  if (!error) return false;
  const message = String(error.message || '');
  if (!NEW_FEE_COLUMNS.some((column) => message.includes(column))) return false;
  return error.code === 'PGRST204'
    || error.code === '42703'
    || /column|schema cache/i.test(message);
};

// One fee field: label, uncontrolled numeric input (the save path reads the
// live DOM via FormData, so the value has to be IN the form), and the
// provenance line in the same muted tone as the fulfillment provenance.
const FeeField = ({ label, name, vendor, percent = false, placeholder = '', hint }) => {
  const rawValue = vendor ? vendor[name] : null;
  const provenanceEntry = vendor
    && vendor.fee_provenance
    && typeof vendor.fee_provenance === 'object'
    ? vendor.fee_provenance[name]
    : null;
  const provenance = feeProvenanceText(provenanceEntry, rawValue);
  return (
    <div className="ve-field">
      <label>{label}</label>
      <input
        type="number"
        name={name}
        min="0"
        max={percent ? '50' : undefined}
        step="0.01"
        inputMode="decimal"
        defaultValue={percent ? feeFractionToPercent(rawValue) : (rawValue ?? '')}
        placeholder={placeholder}
        title={hint || undefined}
      />
      {provenance && (
        <small style={{ fontSize: '.75rem', color: '#8B7B73', marginTop: 4, display: 'block', fontStyle: provenance === 'Not set yet' ? 'italic' : 'normal' }}>
          {provenance}
        </small>
      )}
    </div>
  );
};


const VendorEditor = ({ initialVendorId = null }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [filterTab, setFilterTab] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [quickAddName, setQuickAddName] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);

  // New UX state
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileEditOpen, setMobileEditOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ names: true, ordering: true, fees: false, contact: true, business: false, notes: false });
  const [toast, setToast] = useState(null);

  // Per-vendor purchase-order metadata for the list cards:
  //   ordersByVendor: { [vendorId]: PurchaseOrderRow[] }  (sent orders, newest first)
  //   userNames:      { [userId]: "First L." }             (resolved sender names)
  const [ordersByVendor, setOrdersByVendor] = useState({});
  const [userNames, setUserNames] = useState({});

  // Order-history modal state
  const [historyVendor, setHistoryVendor] = useState(null);
  const [historyRows, setHistoryRows] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Hide the mobile bottom nav while the history sheet is open.
  useHideMobileNav(!!historyVendor);

  // Bumped to force the (partly uncontrolled) edit form to remount with fresh
  // defaultValues after Counter Intelligence edits the open vendor out-of-band.
  const [formRefreshKey, setFormRefreshKey] = useState(0);
  const selectedVendorRef = useRef(null);
  const initialVendorHandledRef = useRef(false);

  // Ordering cycle/cutoff state (used in save payload; managed via other UI)
  const [orderCycle, setOrderCycle] = useState(null);
  const [cutoffTime, setCutoffTime] = useState('');

  const getAliasText = (aliasRow) => normalizeVendorName(aliasRow?.alias_name || aliasRow?.alias || '');

  // Form state
  const [vendorForm, setVendorForm] = useState({
    display_name: '',
    legal_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact2_name: '',
    contact2_email: '',
    contact2_phone: '',
    general_phone: '',
    general_email: '',
    send_orders_to_contact_email: true,
    send_orders_to_general_email: false,
    address: '',
    website: '',
    notes: '',
    payment_terms: '',
    delivery_days: '',
    minimum_order: '',
    minimum_order_amount: '',
    below_minimum_fee: '',
    delivery_fee: '',
    free_delivery_threshold: '',
    fuel_surcharge_amount: '',
    sales_tax_rate: '',
    account_number: '',
    ordering_method: 'email',
    fulfillment_type: 'delivery',
    fulfillment_type_source: 'default',
    lead_days: 0,
    ordering_days: 0,
    cutoff_time: '',
    tags: [],
    status: 'active'
  });

  // Load stylesheet on mount
  useEffect(() => { loadVendorStyles(); }, []);

  useEffect(() => {
    loadCurrentStore();
  }, []);

  useEffect(() => {
    if (currentStore) {
      loadVendors();
      loadOrderSummaries();
    }
  }, [currentStore]);

  // Refresh the per-vendor order metadata after an order is sent elsewhere in
  // the app, so the "last order" line and history stay current without a reload.
  useEffect(() => {
    const onOrdersChanged = () => { if (currentStore) loadOrderSummaries(); };
    window.addEventListener('purchaseOrdersChanged', onOrdersChanged);
    return () => window.removeEventListener('purchaseOrdersChanged', onOrdersChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  // Publish the vendor currently open in the editor so the global Counter
  // Intelligence drawer can resolve "this vendor" without the user naming it.
  useEffect(() => {
    selectedVendorRef.current = selectedVendor;
    if (selectedVendor && selectedVendor.id != null) {
      window.__ciActiveVendor = {
        id: selectedVendor.id,
        display_name: selectedVendor.display_name || selectedVendor.name || '',
      };
    } else {
      window.__ciActiveVendor = null;
    }
  }, [selectedVendor?.id, selectedVendor?.display_name]);

  // Clear the published context when the Vendors page unmounts.
  useEffect(() => () => { window.__ciActiveVendor = null; }, []);

  // When Counter Intelligence applies a vendor edit, refresh the list and, if the
  // edited vendor is the one open on the right, repopulate its form in place.
  useEffect(() => {
    const onAiApplied = async (e) => {
      if (e?.detail?.kind && e.detail.kind !== 'vendor_edit') return;
      const fresh = await loadVendors();
      const open = selectedVendorRef.current;
      if (open && Array.isArray(fresh)) {
        const updated = fresh.find((v) => String(v.id) === String(open.id));
        if (updated) {
          setSelectedVendor(updated);
          await handleEditVendor(updated);
          setFormRefreshKey((k) => k + 1);
        }
      }
    };
    window.addEventListener('counter-intelligence:applied', onAiApplied);
    return () => window.removeEventListener('counter-intelligence:applied', onAiApplied);
    // Re-bind when the store changes so loadVendors closes over the right store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore]);

  // Sync cycle selector when the right-panel vendor changes.
  // Prefer full ordering_cycle_config if already loaded; fall back to bitmask reconstruction.
  useEffect(() => {
    if (selectedVendor) {
      setOrderCycle(selectedVendor.ordering_cycle_config || bitmaskToCycle(selectedVendor.ordering_days || 0));
      setCutoffTime(selectedVendor.cutoff_time || '');
    } else {
      setOrderCycle(null);
      setCutoffTime('');
    }
  }, [selectedVendor?.id]);

  // Deep-link/embedded support: if the page was opened with ?editVendorId=<id>
  // (e.g. from the "Add vendor contact information?" button on the
  // vendor-order-send sheet), or Ordering embedded us with initialVendorId,
  // jump straight into that vendor's edit panel after the list has loaded.
  useEffect(() => {
    if (initialVendorHandledRef.current) return;
    if (loading || !Array.isArray(vendors) || vendors.length === 0) return;
    let targetId = initialVendorId;
    if (!targetId) {
      try {
        const params = new URLSearchParams(window.location.search);
        targetId = params.get('editVendorId');
      } catch (_) {}
    }
    if (!targetId) return;
    // Always strip the param after the first attempt (whether or not we
    // found a match) so a refresh / second render doesn't keep retrying
    // on a stale or invalid id.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('editVendorId');
      window.history.replaceState({}, '', url.toString());
    } catch (_) {}
    const target = vendors.find((v) => String(v?.id) === String(targetId));
    if (!target) return;
    initialVendorHandledRef.current = true;
    // Use the same path as clicking the vendor card: merely loading the form
    // leaves selectedVendor empty and the embedded editor would still show
    // "Select a vendor" on the right.
    selectVendor(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVendorId, loading, vendors]);

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Toggle accordion section
  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const loadCurrentStore = async () => {
    try {
      // Try localStorage first (set by store selector)
      const selectedStoreId = localStorage.getItem('selectedStoreId');
      const storedStore = localStorage.getItem('currentStore');
      if (selectedStoreId) {
        const storeId = parseInt(selectedStoreId, 10);
        let storeName = 'Store';
        try { storeName = JSON.parse(storedStore)?.name || storeName; } catch(e) {}
        setCurrentStore({ id: storeId, name: storeName });
        console.log('✅ Using store from localStorage:', storeId, storeName);
        return;
      }

      // Fall back to Supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user found');
        return;
      }

      const { data: userStores, error: userStoresError } = await supabase
        .from('store_users')
        .select('store_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1);

      if (userStoresError) {
        console.error('Error fetching user stores:', userStoresError);
        return;
      }

      if (userStores && userStores.length > 0) {
        setCurrentStore({ id: userStores[0].store_id });
      }
    } catch (error) {
      console.error('Error loading current store:', error);
    }
  };

  const syncVendorsFromInventory = async () => {
    if (!currentStore) return { discovered: 0, linked: 0 };

    try {
      console.log('🔄 Syncing vendors from inventory...');

      // Get all unique vendor names from inventory
      const { data: inventoryVendors, error: invError } = await supabase
        .from('inventory_items')
        .select('vendor')
        .eq('store_id', currentStore.id)
        .not('vendor', 'is', null)
        .not('vendor', 'eq', '')
        .not('vendor', 'eq', 'NULL');

      if (invError) {
        console.error('Error loading inventory vendors:', invError);
        return { discovered: 0, linked: 0 };
      }

      const uniqueInventoryVendors = [...new Set(inventoryVendors.map(item => item.vendor.trim()))];

      let discoveredCount = 0;
      let linkedCount = 0;
      const resolvedVendorIds = new Map();

      // Process each inventory vendor
      for (const inventoryVendorName of uniqueInventoryVendors) {
        const assignment = await assignVendorToItem({
          supabaseClient: supabase,
          storeId: currentStore.id,
          vendorName: inventoryVendorName,
          stubNote: 'Auto-discovered from inventory data'
        });
        if (!assignment?.vendorId) continue;
        const normalizedInput = normalizeVendorName(inventoryVendorName).toLowerCase();
        resolvedVendorIds.set(normalizedInput, assignment.vendorId);
        if (assignment.matchType === 'created' || assignment.matchType === 'created_or_resolved') {
          discoveredCount++;
        }
      }

      // Link unlinked inventory rows by resolving each vendor string consistently.
      const { data: unlinkedInventoryRows, error: unlinkedError } = await supabase
        .from('inventory_items')
        .select('id, vendor')
        .eq('store_id', currentStore.id)
        .is('vendor_id', null)
        .not('vendor', 'is', null)
        .not('vendor', 'eq', '')
        .not('vendor', 'eq', 'NULL');

      if (unlinkedError) {
        console.error('Error loading unlinked inventory rows:', unlinkedError);
      } else {
        for (const row of (unlinkedInventoryRows || [])) {
          const lookupKey = normalizeVendorName(row.vendor).toLowerCase();
          const vendorId = resolvedVendorIds.get(lookupKey);
          if (!vendorId) continue;
          const { error: linkError } = await supabase
            .from('inventory_items')
            .update({ vendor_id: vendorId })
            .eq('id', row.id)
            .eq('store_id', currentStore.id);
          if (!linkError) linkedCount++;
        }
      }

      console.log(`✅ Vendor sync complete: ${discoveredCount} discovered, ${linkedCount} linked`);
      return { discovered: discoveredCount, linked: linkedCount };

    } catch (error) {
      console.error('Error syncing vendors:', error);
      return { discovered: 0, linked: 0 };
    }
  };

  const loadVendors = async () => {
    if (!currentStore) return;

    setLoading(true);
    try {
      // First, check if vendors table exists and sync from inventory
      const { data: tableCheck, error: tableError } = await supabase
        .from('vendors')
        .select('id')
        .limit(1);

      if (!tableError) {
        // Vendors table exists, sync first then load
        console.log('✅ Vendors table found, syncing from inventory...');
        const syncResult = await syncVendorsFromInventory();

        // Load vendors with aliases and statistics
        const { data: vendorData, error: vendorError } = await supabase
          .from('vendors')
          .select(`
            *,
            vendor_aliases(id, alias, alias_name, created_at)
          `)
          .eq('store_id', currentStore.id)
          .neq('status', 'merged')
          .order('display_name');

        if (!vendorError && vendorData) {
          // Get item counts for each vendor and process aliases
          const vendorsWithCounts = await Promise.all(
            vendorData.map(async (vendor) => {
              const { count: itemCount, error: countError } = await supabase
                .from('inventory_items')
                .select('id', { count: 'exact', head: true })
                .eq('store_id', currentStore.id)
                .eq('vendor_id', vendor.id);

              // Always use display_name as the canonical name (not alias)
              const displayName = vendor.display_name;

              // Calculate vendor status based on contact information (three-state system)
              const hasContactEmail = vendor.contact_email && vendor.contact_email.trim();
              const hasContactName = vendor.contact_name && vendor.contact_name.trim();
              const hasContactPhone = vendor.contact_phone && vendor.contact_phone.trim();
              const contactFieldsCount = [hasContactEmail, hasContactName, hasContactPhone].filter(Boolean).length;

              let vendorStatus = 'incomplete'; // Default: missing contact info
              if (hasContactEmail && (hasContactName || hasContactPhone)) {
                vendorStatus = 'complete'; // Has required contact info
              }

              return {
                ...vendor,
                display_name: displayName,
                name: displayName, // For backward compatibility
                item_count: countError ? 0 : (itemCount || 0),
                vendor_status: vendorStatus,
                last_updated: vendor.updated_at,
                aliases: vendor.vendor_aliases || []
              };
            })
          );

          // Smart tab selection: prioritize vendors that need attention
          const incompleteCount = vendorsWithCounts.filter(v => v.vendor_status === 'incomplete').length;

          if (incompleteCount > 0) {
            setFilterTab('incomplete');
            console.log(`📋 Auto-selected "Incomplete Data" tab - ${incompleteCount} vendors need information`);
          } else {
            setFilterTab('complete');
            console.log(`✅ Auto-selected "Vendor with Contact" tab - all vendors are complete`);
          }

          // Show sync results if any changes were made
          if (syncResult.discovered > 0 || syncResult.linked > 0) {
            console.log(`🔄 Sync results: ${syncResult.discovered} new vendors discovered, ${syncResult.linked} items linked`);
          }

          setVendors(vendorsWithCounts);
          return vendorsWithCounts;
        }
      } else {
        // Fallback: extract vendors from inventory data
        console.log('ℹ️ Vendors table not available, extracting from inventory');

        const { data: inventoryVendors, error: invError } = await supabase
          .from('inventory_items')
          .select('vendor')
          .eq('store_id', currentStore.id)
          .not('vendor', 'is', null)
          .not('vendor', 'eq', '');

        if (invError) {
          console.error('Error loading inventory vendors:', invError);
          return;
        }

        // Get unique vendors and create vendor objects
        const uniqueVendors = [...new Set(inventoryVendors.map(item => item.vendor))];
        const vendorObjects = uniqueVendors.map(vendorName => ({
          id: vendorName.toLowerCase().replace(/\s+/g, '_'),
          display_name: vendorName,
          name: vendorName,
          contact_name: '',
          contact_email: '',
          contact_phone: '',
          address: '',
          website: '',
          notes: '',
          payment_terms: '',
          delivery_days: '',
          minimum_order: '',
          account_number: '',
          ordering_method: 'email',
          item_count: inventoryVendors.filter(item => item.vendor === vendorName).length,
          isLegacy: true // Flag to indicate this is from inventory data
        }));

        setVendors(vendorObjects);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Resolve "First L." names for PO senders whose stored name is missing or is
  // just an email address, using their user_profiles record. Cached in state so
  // repeated renders don't re-fetch. resolveUserProfileCompactName itself is
  // module-cached, so this is cheap on subsequent calls.
  const resolveSenderNames = async (pos) => {
    const idsToResolve = new Set();
    (pos || []).forEach((po) => {
      const hasRealName = !!toFirstLast(po.ordered_by_name);
      if (!hasRealName && po.ordered_by_user_id) idsToResolve.add(po.ordered_by_user_id);
    });
    if (idsToResolve.size === 0) return;
    const resolved = {};
    await Promise.all(Array.from(idsToResolve).map(async (uid) => {
      try {
        const name = await resolveUserProfileCompactName(uid);
        if (name) resolved[uid] = name;
      } catch (_) { /* ignore individual lookup failures */ }
    }));
    if (Object.keys(resolved).length > 0) {
      setUserNames((prev) => ({ ...prev, ...resolved }));
    }
  };

  // Load the store's recent purchase orders once and group the sent ones by
  // vendor (newest first) so each vendor card can show its last order at a
  // glance. A single query keeps this fast even with many vendors.
  const loadOrderSummaries = async () => {
    if (!currentStore) return;
    try {
      const pos = await fetchPurchaseOrders(currentStore.id, { limit: 500 });
      const map = {};
      (pos || []).forEach((po) => {
        if (!po.vendor_id || !wasPurchaseOrderSent(po)) return;
        const key = String(po.vendor_id);
        (map[key] || (map[key] = [])).push(po);
      });
      Object.values(map).forEach((list) => list.sort((a, b) => purchaseOrderTime(b) - purchaseOrderTime(a)));
      setOrdersByVendor(map);
      // Resolve sender names for each vendor's most recent order.
      resolveSenderNames(Object.values(map).map((list) => list[0]).filter(Boolean));
    } catch (error) {
      console.warn('Could not load vendor order summaries:', error?.message || error);
    }
  };

  // Best "who sent it" label: a real name reduced to First L., else a resolved
  // profile name, else a human-readable email local-part — never a raw user id.
  const senderLabel = (po) => (
    toFirstLast(po?.ordered_by_name)
    || userNames[po?.ordered_by_user_id]
    || prettyEmailLocal(po?.ordered_by_email)
    || 'a teammate'
  );

  // Open the "Set up contact information" flow: select the vendor (opens the
  // editor on desktop / slides it in on mobile) with the Contact section open.
  const handleSetupContact = (vendor) => {
    setOpenSections((prev) => ({ ...prev, contact: true }));
    selectVendor(vendor);
  };

  const openHistory = async (vendor) => {
    setHistoryVendor(vendor);
    setHistoryRows(null);
    setHistoryLoading(true);
    try {
      const pos = await fetchPurchaseOrders(currentStore.id, { vendorId: vendor.id, limit: 500 });
      const placed = (pos || []).filter(wasPurchaseOrderSent).sort((a, b) => purchaseOrderTime(b) - purchaseOrderTime(a));
      setHistoryRows(placed);
      resolveSenderNames(placed);
    } catch (error) {
      console.error('Error loading vendor order history:', error);
      setHistoryRows([]);
      showToast(error?.message || 'Could not load order history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryVendor(null);
    setHistoryRows(null);
  };

  // Build the deep-link that reopens a purchase order on the Orders page.
  const purchaseOrderLink = (po) => (
    `orders.html?po=${encodeURIComponent(po.id)}&poNumber=${encodeURIComponent(po.po_number || '')}`
  );

  const handleFormChange = (field, value) => {
    setVendorForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAlias = async () => {
    const newAliasInput = document.getElementById('new-alias');
    const newAlias = normalizeVendorName(newAliasInput?.value || '');

    if (!newAlias || !selectedVendor || !currentStore) {
      return;
    }

    try {
      await createVendorAliasIfNeeded({
        supabaseClient: supabase,
        storeId: currentStore.id,
        vendorId: selectedVendor.id,
        aliasName: newAlias
      });

      // Clear input
      newAliasInput.value = '';

      loadVendors();
    } catch (error) {
      console.error('Error adding alias:', error);
    }
  };

  const handleRemoveAlias = async (aliasId) => {
    if (!aliasId || !selectedVendor) {
      return;
    }

    try {
      const { error } = await supabase
        .from('vendor_aliases')
        .delete()
        .eq('id', aliasId);

      if (error) {
        console.error('Error removing alias:', error);
        return;
      }

      // Update local state
      setSelectedVendor(prev => ({
        ...prev,
        aliases: prev.aliases?.filter(alias => alias.id !== aliasId) || []
      }));

      // Reload vendors to reflect changes
      loadVendors();
    } catch (error) {
      console.error('Error removing alias:', error);
    }
  };

  const handleAddVendor = () => {
    setEditingVendor(null);
    setVendorForm({
      display_name: '',
      legal_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      contact2_name: '',
      contact2_email: '',
      contact2_phone: '',
      general_phone: '',
      general_email: '',
      send_orders_to_contact_email: true,
      send_orders_to_general_email: false,
      address: '',
      website: '',
      notes: '',
      payment_terms: '',
      delivery_days: '',
      minimum_order: '',
      minimum_order_amount: '',
      below_minimum_fee: '',
      delivery_fee: '',
      free_delivery_threshold: '',
      fuel_surcharge_amount: '',
      sales_tax_rate: '',
      account_number: '',
      ordering_method: 'email',
      fulfillment_type: 'delivery',
    fulfillment_type_source: 'default',
      lead_days: 0,
      ordering_days: 0,
      cutoff_time: '',
      tags: [],
      status: 'active'
    });
    setShowAddForm(true);
  };

  const handleEditVendor = async (vendor) => {
    try {
      if (vendor.isLegacy) {
        // This is a legacy vendor from inventory data, use basic info
        setEditingVendor(vendor);
        setVendorForm({
          display_name: vendor.display_name || vendor.name || '',
          legal_name: '',
          contact_name: '',
          contact_email: '',
          contact_phone: '',
          contact2_name: '',
          contact2_email: '',
          contact2_phone: '',
          general_phone: '',
          general_email: '',
          send_orders_to_contact_email: true,
          send_orders_to_general_email: false,
          address: '',
          website: '',
          notes: 'Imported from inventory data',
          payment_terms: '',
          delivery_days: '',
          minimum_order: '',
          minimum_order_amount: '',
          below_minimum_fee: '',
          delivery_fee: '',
          free_delivery_threshold: '',
          fuel_surcharge_amount: '',
          sales_tax_rate: '',
          account_number: '',
          ordering_method: 'email',
          fulfillment_type: 'delivery',
    fulfillment_type_source: 'default',
          lead_days: 0,
          ordering_days: 0,
          cutoff_time: '',
          tags: [],
          status: 'active'
        });
        setShowAddForm(true);
      } else {
        // Load full vendor details from database
        const { data: vendorDetails, error } = await supabase
          .from('vendors')
          .select('*')
          .eq('id', vendor.id)
          .single();

        if (error) {
          console.error('Error loading vendor details:', error);
          alert('Error loading vendor details. Please try again.');
          return;
        }

        setEditingVendor(vendorDetails);
        setVendorForm({
          display_name: vendorDetails.display_name || '',
          legal_name: vendorDetails.legal_name || '',
          contact_name: vendorDetails.contact_name || '',
          contact_email: vendorDetails.contact_email || '',
          contact_phone: vendorDetails.contact_phone || '',
          contact2_name: vendorDetails.contact2_name || '',
          contact2_email: vendorDetails.contact2_email || '',
          contact2_phone: vendorDetails.contact2_phone || '',
          general_phone: vendorDetails.general_phone || '',
          general_email: vendorDetails.general_email || '',
          send_orders_to_contact_email: vendorDetails.send_orders_to_contact_email !== false,
          send_orders_to_general_email: vendorDetails.send_orders_to_general_email === true,
          address: vendorDetails.address || '',
          website: vendorDetails.website || '',
          notes: vendorDetails.notes || '',
          payment_terms: vendorDetails.payment_terms || '',
          delivery_days: vendorDetails.delivery_days || '',
          minimum_order: vendorDetails.minimum_order || '',
          // Fee columns: NULL means unknown, but an explicit 0 is a real
          // answer — `?? ''` (not `|| ''`) keeps the 0 visible in the form.
          minimum_order_amount: vendorDetails.minimum_order_amount ?? '',
          below_minimum_fee: vendorDetails.below_minimum_fee ?? '',
          delivery_fee: vendorDetails.delivery_fee ?? '',
          free_delivery_threshold: vendorDetails.free_delivery_threshold ?? '',
          fuel_surcharge_amount: vendorDetails.fuel_surcharge_amount ?? '',
          sales_tax_rate: feeFractionToPercent(vendorDetails.sales_tax_rate),
          account_number: vendorDetails.account_number || '',
          ordering_method: vendorDetails.ordering_method || 'email',
          fulfillment_type: vendorDetails.fulfillment_type === 'pickup' ? 'pickup' : 'delivery',
          // Anything unrecognized (including a column that predates the
          // migration) reads as unanswered — the safe direction, because the
          // only cost of "unanswered" is being asked once. ANSWERED_SOURCES is
          // the one list; a second inline copy is how 'history' gets asked
          // about again after the backfill already answered it.
          fulfillment_type_source: ANSWERED_SOURCES.includes(vendorDetails.fulfillment_type_source)
            ? vendorDetails.fulfillment_type_source
            : 'default',
          lead_days: vendorDetails.lead_days || 0,
          ordering_days: vendorDetails.ordering_days || 0,
          cutoff_time: vendorDetails.cutoff_time || '',
          tags: vendorDetails.tags || [],
          status: vendorDetails.status || 'active'
        });

        // Initialize cycle selector: prefer full cycle config over bitmask-derived cycle
        const savedCycle = vendorDetails.ordering_cycle_config || null;
        setOrderCycle(savedCycle || bitmaskToCycle(vendorDetails.ordering_days || 0));
        setCutoffTime(vendorDetails.cutoff_time || '');
        setShowAddForm(true);
      }
    } catch (error) {
      console.error('Error loading vendor details:', error);
      alert('Error loading vendor details. Please try again.');
    }
  };

  const extractVendorFormPayload = (formData) => {
    // Fees & charges: empty input → NULL (unknown), explicit 0 preserved.
    // Sales tax is typed as a percent (8.25) and stored as a fraction (0.0825).
    const feeValues = {
      minimum_order_amount: parseFeeAmountInput(formData.get('minimum_order_amount')),
      below_minimum_fee: parseFeeAmountInput(formData.get('below_minimum_fee')),
      delivery_fee: parseFeeAmountInput(formData.get('delivery_fee')),
      free_delivery_threshold: parseFeeAmountInput(formData.get('free_delivery_threshold')),
      fuel_surcharge_amount: parseFeeAmountInput(formData.get('fuel_surcharge_amount')),
      sales_tax_rate: feePercentToFraction(formData.get('sales_tax_rate')),
    };
    // Only fields the user actually CHANGED ride the update (and get a
    // {source:'user'} provenance entry); untouched columns stay out of the
    // payload so a concurrently invoice-captured fee is never reverted, and
    // their existing (e.g. 'invoice') provenance entries survive.
    const changedFees = pickChangedFeeValues(selectedVendor, feeValues);
    const feeProvenance = mergeFeeProvenance(selectedVendor, feeValues);
    return {
    display_name: normalizeVendorName(formData.get('display_name')),
    legal_name: normalizeVendorName(formData.get('legal_name')) || null,
    contact_name: normalizeVendorName(formData.get('contact_name')) || null,
    contact_email: normalizeVendorName(formData.get('contact_email')) || null,
    contact_phone: normalizeVendorName(formData.get('contact_phone')) || null,
    contact2_name: normalizeVendorName(formData.get('contact2_name')) || null,
    contact2_email: normalizeVendorName(formData.get('contact2_email')) || null,
    contact2_phone: normalizeVendorName(formData.get('contact2_phone')) || null,
    general_phone: normalizeVendorName(formData.get('general_phone')) || null,
    general_email: normalizeVendorName(formData.get('general_email')) || null,
    send_orders_to_contact_email: formData.get('send_orders_to_contact_email') === 'on',
    send_orders_to_general_email: formData.get('send_orders_to_general_email') === 'on',
    address: normalizeVendorName(formData.get('address')) || null,
    website: normalizeVendorName(formData.get('website')) || null,
    payment_terms: normalizeVendorName(formData.get('payment_terms')) || null,
    minimum_order: normalizeVendorName(formData.get('minimum_order')) || null,
    ordering_method: selectedVendor?.ordering_method || 'email',
    fulfillment_type: formData.get('fulfillment_type') === 'pickup' ? 'pickup' : 'delivery',
    // ONLY an explicit 'user' promotes an unasked default into an answer.
    // Anything else is left undefined and stripped before the update, so
    // saving an unrelated field can never silently answer the question on the
    // owner's behalf — they would then never be asked.
    fulfillment_type_source: formData.get('fulfillment_type_source') === 'user' ? 'user' : undefined,
    lead_days: selectedVendor?.lead_days ?? 0,
    account_number: selectedVendor?.account_number || null,
    delivery_days: selectedVendor?.delivery_days || null,
    notes: normalizeVendorName(formData.get('notes')) || null,
    status: normalizeVendorName(formData.get('status')) || 'active',
    ordering_days: cycleToBitmask(orderCycle) || 0,
    ordering_cycle_config: orderCycle || null,
    cutoff_time: cutoffTime || null,
    ...changedFees,
    ...(feeProvenance ? { fee_provenance: feeProvenance } : {})
  };
  };

  const saveVendorDetails = async ({ vendorId, previousDisplayName, payload, includeStoreFields = false }) => {
    const nextDisplayName = normalizeVendorName(payload.display_name);
    const updatePayload = {
      ...payload,
      display_name: previousDisplayName || nextDisplayName
    };
    // An `undefined` in a PostgREST payload is still a column in the request.
    // Dropping them is what lets extractVendorFormPayload say "I have no
    // opinion about this column" — see fulfillment_type_source.
    Object.keys(updatePayload).forEach((key) => {
      if (updatePayload[key] === undefined) delete updatePayload[key];
    });
    // The answer carries its own provenance: who chose, and when.
    if (updatePayload.fulfillment_type_source === 'user') {
      updatePayload.fulfillment_type_set_at = new Date().toISOString();
      try {
        const { data: authData } = await supabase.auth.getUser();
        updatePayload.fulfillment_type_set_by = authData?.user?.id ?? null;
      } catch (_) {
        updatePayload.fulfillment_type_set_by = null;
      }
    }
    if (includeStoreFields) {
      updatePayload.store_id = currentStore.id;
    }

    let { error } = await supabase
      .from('vendors')
      .update(updatePayload)
      .eq('id', vendorId);
    if (error && isMissingFeeColumnError(error)) {
      // Behind-migration store: the new fee columns don't exist yet. Retry
      // once without them so the rest of the vendor save still lands.
      const fallbackPayload = { ...updatePayload };
      delete fallbackPayload.fuel_surcharge_amount;
      delete fallbackPayload.sales_tax_rate;
      delete fallbackPayload.fee_provenance;
      ({ error } = await supabase
        .from('vendors')
        .update(fallbackPayload)
        .eq('id', vendorId));
    }
    if (error) throw error;

    if (normalizeVendorName(previousDisplayName).toLowerCase() !== nextDisplayName.toLowerCase()) {
      await renameVendorAndCreateAlias({
        supabaseClient: supabase,
        storeId: currentStore.id,
        vendorId,
        newDisplayName: nextDisplayName
      });
    } else {
      await createVendorAliasIfNeeded({
        supabaseClient: supabase,
        storeId: currentStore.id,
        vendorId,
        aliasName: nextDisplayName
      });
    }
  };

  const handleSaveVendor = async () => {
    if (!vendorForm.display_name.trim()) {
      alert('Vendor name is required');
      return;
    }

    if (!currentStore) {
      alert('No store selected');
      return;
    }

    setSaving(true);
    try {
      // Check if we have a vendors table available
      const { data: tableCheck, error: tableError } = await supabase
        .from('vendors')
        .select('id')
        .limit(1);

      if (!tableError) {
        // Vendors table exists, use it
        // Contact fields are intentionally excluded here; they are written atomically
        // alongside the draft-PO snapshot propagation by saveVendorContactAndPropagate
        // below, ensuring the vendor write and PO updates never diverge.
        const vendorData = {
          display_name: normalizeVendorName(vendorForm.display_name),
          legal_name: normalizeVendorName(vendorForm.legal_name) || null,
          address: normalizeVendorName(vendorForm.address) || null,
          website: normalizeVendorName(vendorForm.website) || null,
          notes: normalizeVendorName(vendorForm.notes) || null,
          payment_terms: normalizeVendorName(vendorForm.payment_terms) || null,
          delivery_days: normalizeVendorName(vendorForm.delivery_days) || null,
          minimum_order: normalizeVendorName(vendorForm.minimum_order) || null,
          // Fees & charges: empty → NULL (unknown), explicit 0 preserved;
          // sales tax typed as percent, stored as 4dp fraction.
          minimum_order_amount: parseFeeAmountInput(vendorForm.minimum_order_amount),
          below_minimum_fee: parseFeeAmountInput(vendorForm.below_minimum_fee),
          delivery_fee: parseFeeAmountInput(vendorForm.delivery_fee),
          free_delivery_threshold: parseFeeAmountInput(vendorForm.free_delivery_threshold),
          fuel_surcharge_amount: parseFeeAmountInput(vendorForm.fuel_surcharge_amount),
          sales_tax_rate: feePercentToFraction(vendorForm.sales_tax_rate),
          account_number: normalizeVendorName(vendorForm.account_number) || null,
          ordering_method: normalizeVendorName(vendorForm.ordering_method) || 'email',
          fulfillment_type: vendorForm.fulfillment_type === 'pickup' ? 'pickup' : 'delivery',
          lead_days: Number.isFinite(Number(vendorForm.lead_days)) ? Number(vendorForm.lead_days) : 0,
          ordering_days: cycleToBitmask(orderCycle) || 0,
          ordering_cycle_config: orderCycle || null,
          cutoff_time: cutoffTime || null,
          status: normalizeVendorName(vendorForm.status) || 'active'
        };
        // Provenance for fee fields the user changed (against the vendor being
        // edited; a brand-new vendor compares against nothing, so every value
        // entered gets a {source:'user'} entry). Omitted when nothing changed.
        const feeProvenance = mergeFeeProvenance(
          editingVendor && !editingVendor.isLegacy ? editingVendor : null,
          vendorData
        );
        if (feeProvenance) vendorData.fee_provenance = feeProvenance;

        let result;
        if (editingVendor && !editingVendor.isLegacy) {
          // Changed-only fee columns on an EXISTING vendor (see
          // pickChangedFeeValues) — the full set stays only for brand-new
          // vendors, where there is no concurrent row to protect.
          const editPayload = { ...vendorData };
          VENDOR_FEE_COLUMNS.forEach((column) => { delete editPayload[column]; });
          Object.assign(editPayload, pickChangedFeeValues(editingVendor, vendorData));
          await saveVendorDetails({
            vendorId: editingVendor.id,
            previousDisplayName: editingVendor.display_name || editingVendor.name,
            payload: editPayload
          });
          result = { data: { id: editingVendor.id }, error: null };
        } else {
          const assignment = await assignVendorToItem({
            supabaseClient: supabase,
            storeId: currentStore.id,
            vendorName: vendorData.display_name,
            stubNote: 'Created from vendor editor',
            useAI: true
          });
          if (!assignment?.vendorId) {
            throw new Error('Unable to create or resolve vendor.');
          }
          await saveVendorDetails({
            vendorId: assignment.vendorId,
            previousDisplayName: assignment.vendorDisplayName || vendorData.display_name,
            payload: vendorData
          });
          result = { data: { id: assignment.vendorId }, error: null };
        }

        if (result.error) {
          console.error('Error saving vendor:', result.error);
          alert('Error saving vendor: ' + result.error.message);
          return;
        }

        const savedVendorId = result.data?.id;
        let draftUpdateMsg = '';
        if (savedVendorId && currentStore?.id) {
          try {
            const contactFields = {
              contact_name: vendorForm.contact_name || null,
              contact_email: vendorForm.contact_email || null,
              contact_phone: vendorForm.contact_phone || null,
              contact2_name: vendorForm.contact2_name || null,
              contact2_email: vendorForm.contact2_email || null,
              contact2_phone: vendorForm.contact2_phone || null,
              general_email: vendorForm.general_email || null,
              general_phone: vendorForm.general_phone || null,
              send_orders_to_contact_email: vendorForm.send_orders_to_contact_email !== false,
              send_orders_to_general_email: vendorForm.send_orders_to_general_email === true,
            };
            const propagateResult = await saveVendorContactAndPropagate(savedVendorId, currentStore.id, contactFields);
            const count = propagateResult?.updatedCount || 0;
            if (count > 0) {
              draftUpdateMsg = ` Updated ${count} open draft${count !== 1 ? 's' : ''} with new contact info.`;
            }
          } catch (propagateErr) {
            alert(`Vendor saved, but failed to update open drafts with new contact: ${propagateErr?.message || 'Unknown error'}`);
          }
        }

        alert((editingVendor ? 'Vendor updated successfully!' : 'Vendor created successfully!') + draftUpdateMsg);
      } else {
        // Vendors table doesn't exist, show message
        alert('Vendors table not set up yet. Please run the vendors table setup first, or contact your administrator.');
        console.log('ℹ️ Vendors table not available. Table error:', tableError);
      }

      // Reload vendors to get updated data
      await loadVendors();

      // Notify other components that vendor data has changed so they can
      // refresh their vendor name display in real time.
      document.dispatchEvent(new CustomEvent('vendorDataChanged', {
        detail: { storeId: currentStore?.id }
      }));

      setShowAddForm(false);
      setEditingVendor(null);

    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Error saving vendor. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingVendor(null);
    setVendorForm({
      display_name: '',
      legal_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      website: '',
      notes: '',
      payment_terms: '',
      delivery_days: '',
      minimum_order: '',
      minimum_order_amount: '',
      below_minimum_fee: '',
      delivery_fee: '',
      free_delivery_threshold: '',
      fuel_surcharge_amount: '',
      sales_tax_rate: '',
      account_number: '',
      ordering_method: 'email',
      fulfillment_type: 'delivery',
      fulfillment_type_source: 'default'
    });
  };

  // Filter vendors based on selected tab + search
  const filteredVendors = vendors.filter(vendor => {
    const q = searchQuery.toLowerCase();
    if (q && !(vendor.display_name || '').toLowerCase().includes(q) && !(vendor.contact_name || '').toLowerCase().includes(q)) return false;
    if (filterTab === 'all') return true;
    if (filterTab === 'incomplete') return vendor.vendor_status === 'incomplete';
    if (filterTab === 'complete') return vendor.vendor_status === 'complete';
    return true;
  });

  // Vendor completeness check
  const isVendorComplete = (v) => !!(v.contact_email && (v.contact_name || v.contact_phone));

  // Per-card contact display + the "Set up contact information" pill gate.
  const contactInfoFor = (v) => {
    const contactName = (v.contact_name || '').trim();
    const contactEmail = (v.contact_email || '').trim() || (v.general_email || '').trim();
    return { contactName, contactEmail, hasContact: !!(contactName || contactEmail) };
  };
  // Help-tooltip triggers render once per concept — on the first card where
  // each element appears — so vendor cards don't collect repeated ⓘ icons.
  const firstCardId = filteredVendors[0]?.id;
  const firstNoContactId = filteredVendors.find(v => !contactInfoFor(v).hasContact)?.id;
  const firstHistoryCapableId = filteredVendors.find(v => !v.isLegacy)?.id;

  const handleUpdateVendor = async (e, vendorId) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const updatedData = extractVendorFormPayload(formData);

    setSaving(true);
    try {
      const existingVendor = vendors.find((vendor) => String(vendor.id) === String(vendorId));
      const previousDisplayName = existingVendor?.display_name || updatedData.display_name;
      const isRename = normalizeVendorName(previousDisplayName).toLowerCase()
        !== normalizeVendorName(updatedData.display_name).toLowerCase();

      // Strip contact fields from the saveVendorDetails payload; the atomic
      // saveVendorContactAndPropagate call below writes them together with
      // the draft-PO snapshot so vendor contact and PO state cannot diverge.
      // eslint-disable-next-line no-unused-vars
      const { contact_name, contact_email, contact_phone,
              contact2_name, contact2_email, contact2_phone,
              general_email, general_phone,
              send_orders_to_contact_email, send_orders_to_general_email,
              ...nonContactData } = updatedData;
      await saveVendorDetails({
        vendorId,
        previousDisplayName,
        payload: nonContactData
      });

      // Reload vendors — loadVendors returns the fresh list so we can update
      // selectedVendor without reading stale state.
      const freshVendors = await loadVendors();
      const freshVendor = (freshVendors || []).find(v => String(v.id) === String(vendorId));
      if (freshVendor) {
        setSelectedVendor({ ...freshVendor, ...updatedData });
      }

      let draftMsg = '';
      if (currentStore?.id) {
        try {
          const contactFields = {
            contact_name: updatedData.contact_name || null,
            contact_email: updatedData.contact_email || null,
            contact_phone: updatedData.contact_phone || null,
            contact2_name: updatedData.contact2_name || null,
            contact2_email: updatedData.contact2_email || null,
            contact2_phone: updatedData.contact2_phone || null,
            general_email: updatedData.general_email || null,
            general_phone: updatedData.general_phone || null,
            send_orders_to_contact_email: updatedData.send_orders_to_contact_email !== false,
            send_orders_to_general_email: updatedData.send_orders_to_general_email === true,
          };
          const propagateResult = await saveVendorContactAndPropagate(vendorId, currentStore.id, contactFields);
          const count = propagateResult?.updatedCount || 0;
          if (count > 0) {
            draftMsg = ` Updated ${count} open draft${count !== 1 ? 's' : ''} with new contact info.`;
          }
        } catch (propagateErr) {
          showToast(`Vendor saved, but failed to update open drafts: ${propagateErr?.message || 'Unknown error'}`, 'error');
        }
      }

      if (isRename) {
        showToast(`✓ Renamed to "${updatedData.display_name}" — all items updated.${draftMsg}`, 'success');
      } else {
        showToast(`✓ Vendor saved.${draftMsg}`, 'success');
      }
    } catch (error) {
      console.error('Error updating vendor:', error);
      showToast(error.message || 'Error updating vendor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAddVendor = async (e) => {
    e.preventDefault();
    const vendorName = quickAddName.trim();

    if (!vendorName) {
      alert('Vendor name is required');
      return;
    }

    setSaving(true);
    try {
      const assignment = await assignVendorToItem({
        supabaseClient: supabase,
        storeId: currentStore.id,
        vendorName,
        stubNote: 'Created from vendor quick-add',
        useAI: true
      });
      if (!assignment?.vendorId) throw new Error('Unable to create vendor.');

      setQuickAddName('');
      await loadVendors();
      const { data } = await supabase
        .from('vendors')
        .select('*')
        .eq('id', assignment.vendorId)
        .single();
      setSelectedVendor(data || null);
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Error adding vendor');
    } finally {
      setSaving(false);
    }
  };

  // Select vendor and open edit on mobile
  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setMobileEditOpen(true);
    // Populate form
    handleEditVendor(vendor);
  };

  const closeMobileEdit = () => {
    setMobileEditOpen(false);
  };

  if (loading) {
    return <div className="ve-edit-empty"><p>Loading vendors…</p></div>;
  }

  if (!currentStore) {
    return <div className="ve-edit-empty"><h3>No Store Selected</h3><p>Please select a store to manage vendors.</p></div>;
  }

  return (
    <div className="ve-layout">
      {/* ── Left Panel: Vendor List ── */}
      <div className="ve-list-panel">
        <div className="ve-list-header">
          <h3>
            Vendors · {currentStore.name}
            <HelpInfoButton helpTopicId="vendor.overview" page="vendors" section="vendor_list" />
          </h3>
          <input className="ve-search-input" type="text" placeholder="Search vendors…"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          <div className="ve-filter-row">
            {[
              { key: 'all', label: `All (${vendors.length})` },
              { key: 'incomplete', label: `Needs Info (${vendors.filter(v => v.vendor_status === 'incomplete').length})` },
              { key: 'complete', label: `Complete (${vendors.filter(v => v.vendor_status === 'complete').length})` }
            ].map(t => (
              <React.Fragment key={t.key}>
                <button className={`ve-filter-pill${filterTab === t.key ? ' active' : ''}`}
                  onClick={() => setFilterTab(t.key)}>{t.label}</button>
                {t.key === 'incomplete' && (
                  <HelpInfoButton helpTopicId="vendor.completeness" page="vendors" section="vendor_filters" style={{ alignSelf: 'center' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Quick-add */}
        <form className="ve-quick-add" onSubmit={(e) => { e.preventDefault(); if (quickAddName.trim()) handleQuickAddVendor(e); }}>
          <input type="text" value={quickAddName} onChange={(e) => setQuickAddName(e.target.value)} placeholder="Add new vendor…" />
          <button type="submit" disabled={!quickAddName.trim() || saving}>+ Add</button>
        </form>

        {/* Vendor cards */}
        <div className="ve-list-scroll">
          {filteredVendors.map(vendor => {
            const vendorOrders = ordersByVendor[String(vendor.id)] || [];
            const lastOrder = vendorOrders[0] || null;
            const { contactName, contactEmail, hasContact } = contactInfoFor(vendor);
            const isSelected = selectedVendor?.id === vendor.id;
            return (
              <div
                key={vendor.id}
                className={`ve-vendor-card${isSelected ? ' selected' : ''}`}
                onClick={() => selectVendor(vendor)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  // Only act when the card itself is focused — let Enter/Space on
                  // the inner pill/history/email controls do their own thing.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectVendor(vendor); }
                }}
              >
                <div className="ve-card-top">
                  <div className="ve-vendor-avatar">{(vendor.display_name || '?')[0].toUpperCase()}</div>
                  <div className="ve-card-heading">
                    <div className="ve-vendor-name">{vendor.display_name || vendor.name}</div>
                    <div className="ve-card-badges">
                      <span
                        className={`ve-status-dot ${vendor.vendor_status === 'complete' ? 'complete' : 'incomplete'}`}
                        title={vendor.vendor_status === 'complete' ? 'Contact info complete' : 'Contact info incomplete'}
                      ></span>
                      {/* How this vendor is bought from, at a glance — and
                          NOTHING at all when nobody has said yet. An unanswered
                          vendor is not a fault: the question is asked once, at
                          the first order, and an amber "not set" badge on the
                          2,500 vendors nobody has been asked about reads as a
                          problem report about a store that is working fine.
                          buyingModePill returns null for that case; a legacy
                          vendor (a synthesised row with a slug for an id) has
                          no setting to report either. */}
                      {(() => {
                        const modePill = vendor.isLegacy ? null : buyingModePill(vendor);
                        if (!modePill) return null;
                        return (
                          <span
                            className={`ve-mode-pill${modePill.tone === 'info' ? ' pickup' : ''}`}
                            title={modePill.title}
                          >
                            {modePill.text}
                          </span>
                        );
                      })()}
                      <span className="ve-item-count">{vendor.item_count} items</span>
                      {vendor.id === firstCardId && (
                        <HelpInfoButton helpTopicId="vendor.item_count" page="vendors" section="vendor_card"
                          context={{ vendor_id: vendor.id, vendor_item_count: vendor.item_count }} />
                      )}
                    </div>
                  </div>
                </div>

                <div className="ve-card-contact">
                  {hasContact ? (
                    <>
                      <div className="ve-contact-row">
                        <PersonIcon />
                        <span className="ve-contact-text">{contactName || <span className="ve-muted">No contact name</span>}</span>
                      </div>
                      <div className="ve-contact-row">
                        <MailIcon />
                        {contactEmail
                          ? <a className="ve-contact-text ve-contact-email" href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()}>{contactEmail}</a>
                          : <span className="ve-contact-text ve-muted">No email</span>}
                      </div>
                    </>
                  ) : (
                    <>
                    <button
                      type="button"
                      className="ve-contact-pill"
                      onClick={(e) => { e.stopPropagation(); handleSetupContact(vendor); }}
                    >
                      <span className="ve-contact-pill-plus" aria-hidden="true">＋</span>
                      Set up contact information
                    </button>
                    {vendor.id === firstNoContactId && (
                      <HelpInfoButton helpTopicId="vendor.contact_setup" page="vendors" section="vendor_card"
                        context={{ vendor_id: vendor.id, vendor_status: vendor.vendor_status }} />
                    )}
                    </>
                  )}
                </div>

                <div className="ve-card-footer">
                  <div className="ve-card-order">
                    <ClockIcon />
                    {lastOrder ? (
                      <span className="ve-card-order-text">
                        Last order <strong>{formatPoDate(lastOrder.sent_at || lastOrder.created_at)}</strong>
                        <span className="ve-card-order-sep"> · </span>
                        <strong>{formatPoCurrency(lastOrder.total)}</strong>
                        <span className="ve-card-order-sep"> · </span>
                        by {senderLabel(lastOrder)}
                      </span>
                    ) : (
                      <span className="ve-muted">No orders sent</span>
                    )}
                    {vendor.id === firstCardId && (
                      <HelpInfoButton helpTopicId="vendor.last_order" page="vendors" section="vendor_card"
                        context={{ vendor_id: vendor.id }} />
                    )}
                  </div>
                  {/* Always offer history for real vendors: the card's "last
                      order" line is derived from a bounded recent-orders window,
                      so a vendor with older orders may show "No orders sent"
                      here — the modal does an authoritative per-vendor lookup.
                      Legacy inventory-stub vendors have no id and no POs. */}
                  {!vendor.isLegacy && (
                    <button
                      type="button"
                      className="ve-history-btn"
                      onClick={(e) => { e.stopPropagation(); openHistory(vendor); }}
                    >
                      <ReceiptIcon />
                      Order history
                    </button>
                  )}
                  {vendor.id === firstHistoryCapableId && (
                    <HelpInfoButton helpTopicId="vendor.order_history" page="vendors" section="vendor_card"
                      context={{ vendor_id: vendor.id }} />
                  )}
                </div>
              </div>
            );
          })}
          {filteredVendors.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#8B7B73', fontSize: '.85rem' }}>
              {searchQuery ? 'No vendors match your search.' : 'No vendors found.'}
            </div>
          )}
        </div>

        <div className="ve-list-footer">Showing {filteredVendors.length} of {vendors.length} vendors</div>
      </div>

      {/* ── Right Panel: Edit Form ── */}
      <div className={`ve-edit-panel${mobileEditOpen ? ' mobile-open' : ''}`}>
        {selectedVendor ? (
          <>
            <div className="ve-edit-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button className="ve-back-btn" onClick={closeMobileEdit}>← Back</button>
                <h3>{selectedVendor.display_name}</h3>
              </div>
              <button className="ve-save-btn" disabled={saving}
                onClick={() => handleUpdateVendor({ preventDefault: () => {}, target: document.querySelector('#vendor-form') }, selectedVendor.id)}>
                {saving ? 'Saving…' : '✓ Save'}
              </button>
            </div>

            <div className="ve-edit-body">
              <form id="vendor-form" key={`${selectedVendor.id}-${formRefreshKey}`}>

                {/* ── Names & Aliases ── */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('names')}>
                    <span className="ve-section-title">Names & Aliases</span>
                    <span className={`ve-section-toggle${openSections.names ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.names ? ' open' : ''}`}>
                    <div className="ve-field-row">
                      <div className="ve-field"><label>Display Name</label><input type="text" name="display_name" defaultValue={selectedVendor.display_name} placeholder="Display Name" /></div>
                      <div className="ve-field"><label>Legal Name</label><input type="text" name="legal_name" defaultValue={selectedVendor.legal_name || ''} placeholder="Legal / Full Name" /></div>
                    </div>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4, display: 'block' }}>Aliases</label>
                    <div className="ve-alias-list">
                      {selectedVendor.aliases && selectedVendor.aliases.length > 0 ? (
                        selectedVendor.aliases.map((alias, i) => (
                          <span key={i} className="ve-alias-chip">
                            {getAliasText(alias)}
                            <button type="button" className="ve-alias-remove" onClick={() => handleRemoveAlias(alias.id)}>×</button>
                          </span>
                        ))
                      ) : (
                        <span style={{ fontSize: '.8rem', color: '#8B7B73', fontStyle: 'italic' }}>No aliases yet</span>
                      )}
                    </div>
                    <div className="ve-field-row">
                      <div className="ve-field"><input type="text" id="new-alias" placeholder="Add alias (e.g. Freds Beer LA)" /></div>
                      <div className="ve-field narrow" style={{ flex: '0 0 80px' }}>
                        <button type="button" className="ve-save-btn" style={{ width: '100%', marginTop: 0 }} onClick={handleAddAlias}>+ Add</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Ordering ──
                    How you buy from a vendor decides what an order even IS for
                    them — an emailed purchase order, or a shopping list you
                    take to the shop. It used to sit unlabelled at the bottom of
                    a collapsed "Business Details" section; it belongs here,
                    open, near the top. */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('ordering')}>
                    <span className="ve-section-title">Ordering</span>
                    <span className={`ve-section-toggle${openSections.ordering ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.ordering ? ' open' : ''}`}>
                    <div className="ve-field-row">
                      <div className="ve-field">
                        <label>How do you buy from this vendor?</label>
                        <select
                          name="fulfillment_type"
                          value={vendorForm.fulfillment_type}
                          onChange={(e) => setVendorForm({
                            ...vendorForm,
                            fulfillment_type: e.target.value,
                            // Touching the control IS the answer. Nothing else
                            // in this form may promote an unasked default.
                            fulfillment_type_source: 'user',
                          })}
                        >
                          <option value="delivery">{DELIVERY_ANSWER_LABEL}</option>
                          <option value="pickup">{PICKUP_ANSWER_LABEL}</option>
                        </select>
                        {/* The save path reads the live DOM via FormData, so the
                            answer has to be IN the form, not only in state. */}
                        <input type="hidden" name="fulfillment_type_source" value={vendorForm.fulfillment_type_source || 'default'} />
                        {/* Where the current value came from, in plain words:
                            "You set this" / "From your order history" / "From
                            your imported vendor details" / "Not set yet". A
                            setting whose provenance is invisible is a setting
                            nobody trusts — especially one the history backfill
                            may have answered on the owner's behalf. */}
                        <small style={{ fontSize: '.75rem', color: '#8B7B73', marginTop: 4, display: 'block' }}>
                          <strong style={{ fontWeight: 600 }}>{buyingModeSourceText(vendorForm)}</strong>
                          {' · '}
                          {buyingModeExplanation(vendorForm)}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Fees & charges ──
                    The vendor's standing charges: minimums, delivery/fuel
                    fees, and the sales tax rate applied to taxable lines.
                    Invoice imports fill these ONLY while they are blank; a
                    value a person typed here is never overwritten, and each
                    field says where its value came from (fee_provenance). */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('fees')}>
                    <span className="ve-section-title">Fees &amp; charges</span>
                    <span className={`ve-section-toggle${openSections.fees ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.fees ? ' open' : ''}`}>
                    <small style={{ fontSize: '.75rem', color: '#8B7B73', display: 'block', marginBottom: 10 }}>
                      Filled automatically from invoices when blank — your edits always win.
                    </small>
                    <div className="ve-field-row">
                      <FeeField label="Minimum order ($)" name="minimum_order_amount" vendor={selectedVendor} />
                      <FeeField label="Small-order fee ($)" name="below_minimum_fee" vendor={selectedVendor}
                        hint="Charged when an order is under the minimum"
                        placeholder="Charged under the minimum" />
                    </div>
                    <div className="ve-field-row">
                      <FeeField label="Delivery fee ($)" name="delivery_fee" vendor={selectedVendor} />
                      <FeeField label="Free delivery over ($)" name="free_delivery_threshold" vendor={selectedVendor} />
                    </div>
                    <div className="ve-field-row">
                      <FeeField label="Fuel surcharge ($)" name="fuel_surcharge_amount" vendor={selectedVendor} />
                      <FeeField label="Sales tax rate (%)" name="sales_tax_rate" vendor={selectedVendor} percent
                        hint="Applied to taxable lines — e.g. 8.25 for 8.25%"
                        placeholder="e.g. 8.25" />
                    </div>
                  </div>
                </div>

                {/* ── Contact Info ── */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('contact')}>
                    <span className="ve-section-title">Contact Information</span>
                    <span className={`ve-section-toggle${openSections.contact ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.contact ? ' open' : ''}`}>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 6, display: 'block' }}>Primary Contact (Sales Person)</label>
                    <div className="ve-field-row">
                      <div className="ve-field"><label>Name</label><input type="text" name="contact_name" value={vendorForm.contact_name} onChange={(e) => setVendorForm({ ...vendorForm, contact_name: e.target.value })} placeholder="Contact Name" /></div>
                      <div className="ve-field"><label>Email</label><input type="email" name="contact_email" value={vendorForm.contact_email} onChange={(e) => setVendorForm({ ...vendorForm, contact_email: e.target.value })} placeholder="Email" /></div>
                      <div className="ve-field narrow"><label>Phone</label><input type="tel" name="contact_phone" value={vendorForm.contact_phone} onChange={(e) => setVendorForm({ ...vendorForm, contact_phone: e.target.value })} placeholder="Phone" /></div>
                    </div>
                    <div className="ve-field-row" style={{ marginTop: 4, marginBottom: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', color: '#5A4A42', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="send_orders_to_contact_email"
                          checked={!!vendorForm.send_orders_to_contact_email}
                          onChange={(e) => setVendorForm({ ...vendorForm, send_orders_to_contact_email: e.target.checked })}
                        />
                        Send purchase orders to this email
                      </label>
                    </div>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '.3px', margin: '10px 0 6px', display: 'block' }}>Secondary Contact</label>
                    <div className="ve-field-row">
                      <div className="ve-field"><label>Name</label><input type="text" name="contact2_name" defaultValue={selectedVendor.contact2_name || ''} placeholder="Contact Name" /></div>
                      <div className="ve-field"><label>Email</label><input type="email" name="contact2_email" defaultValue={selectedVendor.contact2_email || ''} placeholder="Email" /></div>
                      <div className="ve-field narrow"><label>Phone</label><input type="tel" name="contact2_phone" defaultValue={selectedVendor.contact2_phone || ''} placeholder="Phone" /></div>
                    </div>
                    <label style={{ fontSize: '.75rem', fontWeight: 600, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '.3px', margin: '10px 0 6px', display: 'block' }}>General</label>
                    <div className="ve-field-row">
                      <div className="ve-field"><label>Phone</label><input type="tel" name="general_phone" defaultValue={selectedVendor.general_phone || ''} placeholder="General Phone" /></div>
                      <div className="ve-field"><label>Email</label><input type="email" name="general_email" value={vendorForm.general_email} onChange={(e) => setVendorForm({ ...vendorForm, general_email: e.target.value })} placeholder="General Email" /></div>
                    </div>
                    <div className="ve-field-row" style={{ marginTop: 4 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', color: '#5A4A42', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="send_orders_to_general_email"
                          checked={!!vendorForm.send_orders_to_general_email}
                          onChange={(e) => setVendorForm({ ...vendorForm, send_orders_to_general_email: e.target.checked })}
                        />
                        Send purchase orders to this email
                      </label>
                    </div>
                    {!vendorForm.send_orders_to_contact_email && !vendorForm.send_orders_to_general_email ? (
                      <div style={{ marginTop: 8, fontSize: '.8rem', color: '#B45309', background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6, padding: '6px 10px' }}>
                        Tick at least one box above so purchase orders have somewhere to be sent.
                      </div>
                    ) : null}
                    {vendorForm.send_orders_to_contact_email && vendorForm.send_orders_to_general_email ? (
                      <div style={{ marginTop: 8, fontSize: '.8rem', color: '#5A4A42' }}>
                        Purchase orders will be sent to both email addresses.
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* ── Business Details ── */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('business')}>
                    <span className="ve-section-title">Business Details</span>
                    <span className={`ve-section-toggle${openSections.business ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.business ? ' open' : ''}`}>
                    <div className="ve-field-row">
                      <div className="ve-field" style={{ flex: 2 }}><label>Address</label><textarea name="address" defaultValue={selectedVendor.address || ''} placeholder="Address" rows="2" /></div>
                      <div className="ve-field"><label>Website</label><input type="url" name="website" defaultValue={selectedVendor.website || ''} placeholder="https://…" /></div>
                    </div>
                    <div className="ve-field-row">
                      <div className="ve-field"><label>Payment Terms</label><input type="text" name="payment_terms" defaultValue={selectedVendor.payment_terms || ''} placeholder="e.g. Net 30" /></div>
                      <div className="ve-field"><label>Min. Order</label><input type="text" name="minimum_order" defaultValue={selectedVendor.minimum_order || ''} placeholder="e.g. $100" /></div>
                    </div>
                  </div>
                </div>

                {/* ── Notes & Status ── */}
                <div className="ve-section">
                  <div className="ve-section-header" onClick={() => toggleSection('notes')}>
                    <span className="ve-section-title">Notes & Status</span>
                    <span className={`ve-section-toggle${openSections.notes ? ' open' : ''}`}>▼</span>
                  </div>
                  <div className={`ve-section-body${openSections.notes ? ' open' : ''}`}>
                    <div className="ve-field" style={{ marginBottom: 10 }}>
                      <label>Notes</label>
                      <textarea name="notes" defaultValue={selectedVendor.notes || ''} placeholder="Notes…" rows="3" />
                    </div>
                    <div className="ve-field">
                      <label>Status</label>
                      <select name="status" defaultValue={selectedVendor.status || 'active'}>
                        <option value="active">Active</option><option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>

              </form>
            </div>

            {/* Mobile-only fixed save footer */}
            <div className="ve-save-footer">
              <button className="ve-save-btn" disabled={saving}
                onClick={() => handleUpdateVendor({ preventDefault: () => {}, target: document.querySelector('#vendor-form') }, selectedVendor.id)}>
                {saving ? 'Saving…' : '✓ Save Changes'}
              </button>
            </div>
          </>
        ) : (
          <div className="ve-edit-empty">
            <h3>Select a vendor</h3>
            <p>Choose a vendor from the list to edit their information.</p>
          </div>
        )}
      </div>

      {/* Order-history modal */}
      {historyVendor && (
        <div className="veh-overlay" onClick={closeHistory}>
          <div className="veh-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Order history for ${historyVendor.display_name}`}>
            <div className="veh-header">
              <div className="veh-header-text">
                <h3>Order history</h3>
                <p>
                  {historyVendor.display_name}
                  {Array.isArray(historyRows) ? ` · ${historyRows.length} order${historyRows.length === 1 ? '' : 's'}` : ''}
                </p>
              </div>
              <button type="button" className="veh-close" onClick={closeHistory} aria-label="Close order history">&times;</button>
            </div>

            <div className="veh-body">
              {historyLoading && <div className="veh-empty">Loading order history…</div>}
              {!historyLoading && Array.isArray(historyRows) && historyRows.length === 0 && (
                <div className="veh-empty">No orders have been sent to this vendor yet.</div>
              )}
              {!historyLoading && Array.isArray(historyRows) && historyRows.map((po) => {
                const statusKey = po.delivery_status;
                const statusMeta = ORDER_STATUS_STYLES[statusKey];
                const itemCount = Number(po.line_count || 0);
                return (
                  <div className="veh-row" key={po.id}>
                    <div className="veh-row-main">
                      <div className="veh-row-top">
                        <span className="veh-row-date">{formatPoDate(po.sent_at || po.created_at)}</span>
                        {statusMeta && (
                          <span className="veh-status" style={statusPillStyle(statusKey)}>{statusMeta.label}</span>
                        )}
                      </div>
                      {po.po_number && <div className="veh-row-po">{po.po_number}</div>}
                      <div className="veh-row-meta">
                        <span className="veh-amount">{formatPoCurrency(po.total)}</span>
                        <span className="veh-dot">·</span>
                        <span>{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                        <span className="veh-dot">·</span>
                        <span>by {senderLabel(po)}</span>
                      </div>
                    </div>
                    <a className="veh-view" href={purchaseOrderLink(po)}>View order →</a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && <div className={`ve-toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

export default VendorEditor;
